import {
    currentActiveChatbotThreadTypeOld,
    currentActiveChatbotThreadTypeNew,
    manualAgentData,
    currentActiveThreadChatbotThreadData,
    newThreadInfo,
    metIntegrationInstagramDataNew,
    metaIntegrationFaceBookDataNew,
    metaIntegrationWhatsAppDataNew,
    chatbot_action_execution_data,
    threadTickets,
    integratedChannel,
    getChatbotResponseModel,
    getSpecificChatbotThreadData,
    UserMeResponseDataModel,
    vendorDataResponse,
    ticketFieldData,
    ticketStatusData,
    organizationUserDataModel,
    getOrganizationDetailDataModel,
    userAssignmentRoleDataModel,
    threadMetaData,
    closedChats,
    whatsAppChatsData,
    apiRoutesPermissionAssignmentsDataModel,
    CannedResponseTemplate,
    uploadedMedia,
    sendTemplatePayload,
    sendCannedResponsePayload,
    ThreadMsgFields,
    ChatbotActionPayloadItem,
} from "../../models/axiosModel";
import { InboxControlsState, RuleBasedThread, ThreadManualAgentData } from "../../types/ruleBasedInbox";
import { ACTIVE_THREAD_HOURS_THRESHOLD, ACTIVE_TICKET_STATUS, AGENT_ACTIVITY_ACTION_TYPE, agentInfoActionTypes, CANNED_RESPONSE_VARIABLES, CHATBOT_ACTION_TYPE, DEFAULT_ACTION_TYPES, DEFAULT_INBOX_CONTROLS_STATE, defaultChatThemeColors, FEEDBACK_KEY_LABELS, FILTER_URL_KEYS, INSTAGRAM_ACTION_TYPES, METADATA_KEYS, NON_COPYABLE_BOT_ACTION_TYPES, patternToRemoveSource, RECENT_CHAT_THRESHOLD_MINUTES, THREAD_CHANNEL_NAMES, USER_MESSAGE_ACTION_TYPES, userMessagesActionTypes, VALID_MESSAGE_ACTION_TYPES } from "./constants";
import { blackColor, MANUAL_OVERRIDE_BTN_TEXT_COLORS, primaryColor, tertiaryColor, whiteColor } from "../../utils/colors";
import { generateUUID, getEffectiveUserRoleName, getValueOrDefault, hasAIChatbotPageAccess, hasInboxAdminAccess, hasInboxReadAccess, isAdmin, isJsonString, isOrganizationRoleAgent, isWaSmbUser, ORGANIZATION_ROLES, toScreamingSnakeCase, USER_ASSIGNMENT_ROLES } from "../../utils/constants";
import { ACTION_TYPE_NAME } from "../../components/dashboard/flowBuilder/actionTypeName";
import parsePhoneNumberFromString from "libphonenumber-js";
import { getVendorField } from "../../utils/whiteLabelling";
import { THREAD_STATUS } from "./threadInfo/components/threadStatus/customerConversationAssignedStatus";
import moment from "moment";
import { useRuleBasedInboxStore } from "../../store/ruleBasedInboxStore";


/**
 * Check if a timestamp is within the recent threshold
 * @param timestamp - ISO timestamp string to check
 * @returns true if the timestamp is within RECENT_CHAT_THRESHOLD_MINUTES from now
 */
export const isWithinRecentThreshold = (timestamp: string): boolean => {
    if (!timestamp) return false;
    try {
        const now = Date.now();
        const time = new Date(timestamp).getTime();
        const diffMs = now - time;
        const thresholdMs = RECENT_CHAT_THRESHOLD_MINUTES * 60 * 1000;
        return diffMs >= 0 && diffMs <= thresholdMs;
    } catch {
        return false;
    }
};

/**
 * Get default inbox controls state based on user role
 * For Organization Agents, both flagAssignedToMe and flagUnassigned are set to true by default
 * @param userDetail - User detail object containing user_organization_data
 * @returns Default inbox controls state
 */
export const getDefaultInboxControlsState = (userDetail: any): InboxControlsState => {
    // Check if user is an Organization Agent using the existing helper function
    const isAgent = isOrganizationRoleAgent(userDetail);
    
    return {
        ...DEFAULT_INBOX_CONTROLS_STATE,
        flagAssignedToMe: isAgent ? true : DEFAULT_INBOX_CONTROLS_STATE.flagAssignedToMe,
        flagUnassigned: isAgent ? true : DEFAULT_INBOX_CONTROLS_STATE.flagUnassigned,
    };
};

// Check If channelName is Website or Chronox AI Web App
export const isChannelNameWebsiteOrChronox = (channelName: string) => {
    const allowedChannelNames = [
        THREAD_CHANNEL_NAMES['WEBSITE']?.toLowerCase(), 
        THREAD_CHANNEL_NAMES['CHRNOX_AI_WEB_APP']?.toLowerCase(), 
        THREAD_CHANNEL_NAMES['CHRONOX_AI_WEB_APP']?.toLowerCase()
    ];
    return allowedChannelNames?.includes(channelName?.toLowerCase() ?? '');
}

export const getRelatedChannelName = (channelName: string) => {
    const channel = channelName?.toLowerCase();
    switch(channel){
        case THREAD_CHANNEL_NAMES['CHRNOX_AI_WEB_APP']?.toLowerCase():
        case THREAD_CHANNEL_NAMES['CHRONOX_AI_WEB_APP']?.toLowerCase():
            return 'Chronox AI WebApp';
        case THREAD_CHANNEL_NAMES['WHATSAPP']?.toLowerCase():
            return 'WhatsApp';
        default: 
            // Capitalize first letter of each word.
            return channel?.replace(/\b\w/g, (char) => char.toUpperCase());
    }
}

/**
 * Helper function to get metadata value by type name
 */
export const getMetadataValue = (
    metadata: { metadata_type_name: string; metadata_value: string }[],
    typeName: string
): string => {
    const found = metadata.find(
        (m) => m?.metadata_type_name?.toLowerCase() === typeName?.toLowerCase()
    );
    return found?.metadata_value || "";
};

/**
 * Helper function to format relative time (deprecated - use formatThreadTime instead)
 */
export const formatRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d`;

    // Format as date for older messages
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
};

const parseTimezoneOffsetMinutes = (timezoneHour: string | null): number => {
    if (!timezoneHour) return 0;

    const offsetMatch = timezoneHour.match(/^UTC([+-])(\d{1,2}):?(\d{2})?$/);
    if (!offsetMatch) return 0;

    const sign = offsetMatch[1] === "+" ? 1 : -1;
    const hours = parseInt(offsetMatch[2], 10);
    const minutes = parseInt(offsetMatch[3] || "0", 10);
    return sign * (hours * 60 + minutes);
};

/**
 * Check if a thread timestamp falls on today's date in the user's timezone.
 * Matches the "today vs date" display logic used in the thread list.
 */
export const isThreadTimestampToday = (timestamp: string, timezoneHour: string | null): boolean => {
    if (!timestamp) return false;

    try {
        const userOffsetMinutes = parseTimezoneOffsetMinutes(timezoneHour);
        const timestampInUserTz = new Date(new Date(timestamp).getTime() + userOffsetMinutes * 60 * 1000);
        const nowInUserTz = new Date(Date.now() + userOffsetMinutes * 60 * 1000);

        return (
            timestampInUserTz.getUTCFullYear() === nowInUserTz.getUTCFullYear() &&
            timestampInUserTz.getUTCMonth() === nowInUserTz.getUTCMonth() &&
            timestampInUserTz.getUTCDate() === nowInUserTz.getUTCDate()
        );
    } catch {
        return false;
    }
};

/**
 * Helper function to format thread time based on user timezone
 * - If the date is today (according to user's timezone): Shows time in 24-hour format (HH:mm)
 * - If the date is not today: Shows date in dd/mm/yy format
 */
export const formatThreadTime = (timestamp: string, timezoneHour: string | null): { fullDateTimeText: string, displayText: string } => {
    const emptyTime = {
        fullDateTimeText: '', 
        displayText: ''
    }
    if (!timestamp) return emptyTime;

    try {
        const userOffsetMinutes = parseTimezoneOffsetMinutes(timezoneHour);

        // Parse the timestamp (assumed to be in UTC or ISO format)
        const timestampDate = new Date(timestamp);

        // Get UTC time components
        const timestampUtcTime = timestampDate.getTime();

        // Convert to user's timezone by adding the offset
        // Note: userOffsetMinutes is positive for timezones ahead of UTC (e.g., UTC+5:30 = +330)
        const timestampInUserTz = new Date(timestampUtcTime + userOffsetMinutes * 60 * 1000);

        const isToday = isThreadTimestampToday(timestamp, timezoneHour);

        // Different day - show date in dd/mm/yy format
        const day = timestampInUserTz.getUTCDate().toString().padStart(2, '0');
        const month = (timestampInUserTz.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = timestampInUserTz.getUTCFullYear().toString().slice(-2);
        const hours = timestampInUserTz.getUTCHours().toString().padStart(2, '0');
        const minutes = timestampInUserTz.getUTCMinutes().toString().padStart(2, '0');

        const dmy = `${day}/${month}/${year}`;
        const hm = `${hours}:${minutes}`;
        let fullDateTimeText = `${dmy} ${hm}`;
        let displayText = '';

        if (isToday) {
            // Same day - show time in 24-hour format (HH:mm)
            displayText = `${hm}`;
            fullDateTimeText = `Today ${displayText}`
        } else {
            displayText = dmy;
        }
        return {
            fullDateTimeText: fullDateTimeText,
            displayText: displayText
        }
    } catch (error) {
        console.error("Error formatting thread time:", error);
        return emptyTime;
    }
}; 

/**
 * Helper function to get display name from thread metadata
 */
export const getThreadDisplayName = (
    metadata: { metadata_type_name: string; metadata_value: string }[]
): string => {
    const name = getMetadataValue(metadata, METADATA_KEYS.NAME);
    const phoneNumber = getMetadataValue(metadata, METADATA_KEYS.PHONE_NUMBER);

    if (name && name.trim()) {
        return name;
    }

    if (phoneNumber) {
        // Format phone number with country code
        return `+${phoneNumber}`;
    }

    return "Unknown";
};

/**
 * Helper function to get phone number from thread metadata
 */
export const getThreadPhoneNumber = (
    metadata: { metadata_type_name: string; metadata_value: string }[]
): string => {
    const phoneNumber = getMetadataValue(metadata, METADATA_KEYS.PHONE_NUMBER);
    return phoneNumber ? `+${phoneNumber}` : "";
};

/**
 * Helper function to get chatbot user country from thread metadata
 */
export const getThreadChatbotUserCountry = (
    metadata: { metadata_type_name: string; metadata_value: string }[]
): string => {
    const country = getMetadataValue(metadata, METADATA_KEYS.CHATBOT_USER_COUNTRY);
    return country ?? "";
};

/**
 * Helper function to get chatbot user country from thread metadata
 */
export const getThreadChatbotUserCountryCode = (
    metadata: { metadata_type_name: string; metadata_value: string }[]
): string => {
    const countryCode = getMetadataValue(metadata, METADATA_KEYS.CHATBOT_USER_COUNTRY_CODE);
    return countryCode ?? "";
};

/**
 * Get initials from first and last name
 */
export const getInitials = (firstName?: string | null, lastName?: string | null): string => {
    const first = firstName?.trim()?.[0]?.toUpperCase() || "";
    const last = lastName?.trim()?.[0]?.toUpperCase() || "";

    if (first && last) return `${first}${last}`;
    if (first) return first;
    if (last) return last;
    return "A"; // Default for Agent
};

/**
 * Get sender info for last message
 */
export const getLastMessageSender = (
    actionTypeName: string,
    actionExecutionData: Record<string, any> | undefined,
    userAssignmentId: number | null,
    currentUserAssignmentId: number | null,
    senderFirstName?: string | null,
    senderLastName?: string | null,
    userName?: string | null // User's name from thread metadata
): { type: "user" | "you" | "agent" | "bot"; label: string } => {
    // If it's a user message action type, the sender is the user
    if (isUserMessage(actionTypeName, actionExecutionData as chatbot_action_execution_data)) {
        // Get user's first name or fallback to "User"
        const userLabel = userName?.split(" ")?.[0]?.trim() || "User";
        return { type: "user", label: userLabel };
    }

    // It's a bot/agent/system message
    if (userAssignmentId === null) {
        // No agent assigned, it's the system/bot
        return { type: "bot", label: "Bot" };
    }

    // Check if it's the current logged-in user
    if (currentUserAssignmentId && userAssignmentId === currentUserAssignmentId) {
        return { type: "you", label: "You" };
    }

    // It's another agent - show initials
    const agentInitials = getInitials(senderFirstName, senderLastName);
    return { type: "agent", label: agentInitials };
};

/**
 * Truncate text to a max length with ellipsis
 */
const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text) return "";
    // Remove markdown formatting and newlines for preview
    const cleanText = text
        .replace(/\*+/g, "") // Remove asterisks (bold)
        .replace(/\n+/g, " ") // Replace newlines with space
        .replace(/\s+/g, " ") // Collapse multiple spaces
        .trim();

    if (cleanText.length <= maxLength) return cleanText;
    return cleanText.substring(0, maxLength).trim() + "...";
};

/**
 * Extract message text from last_message based on action_type_name
 */
export const getLastMessageText = (
    actionTypeName: string,
    actionExecutionData: Record<string, any> | undefined
): string => {
    if (!actionExecutionData) return "";

    const actionType = actionTypeName?.toLowerCase() ?? "";

    // Switch case for different action types
    switch (actionType) {
        // User text messages
        case USER_MESSAGE_ACTION_TYPES['TEXT']?.toLowerCase():
        case INSTAGRAM_ACTION_TYPES["SEND_IG_DM_TEXT"]?.toLowerCase():
            return truncateText(actionExecutionData.message || "");

        // User media messages
        case USER_MESSAGE_ACTION_TYPES['MEDIA']?.toLowerCase():
            return "📎 Media";

        // WhatsApp template messages
        case ACTION_TYPE_NAME['TEMPLATE']?.toLowerCase():
            // Check for media header
            if (actionExecutionData.wa_header_media_chronox_storage_url) {
                return "📎 " + truncateText(actionExecutionData.wa_body_text || "Template message");
            }
            return truncateText(actionExecutionData.wa_body_text || actionExecutionData.wa_header_text || "Template message");
        case ACTION_TYPE_NAME['CAROUSEL_TEMPLATE']?.toLowerCase():
            return truncateText(actionExecutionData?.wa_header_text || "Carousel Template");
        // WhatsApp text messages sent by bot/agent
        case ACTION_TYPE_NAME['TEXT']?.toLowerCase():
            return truncateText(actionExecutionData.message || actionExecutionData.wa_body_text || "");

        // WhatsApp media messages sent by bot/agent
        case ACTION_TYPE_NAME['SEND_MEDIA']?.toLowerCase():
            return "📎 " + truncateText(actionExecutionData.caption || actionExecutionData.message || "Media");

        // Await user message (system action)
        case "await_user_message":
            return "Waiting for response...";

        // Manual agent actions
        case AGENT_ACTIVITY_ACTION_TYPE['ASSIGN']?.toLowerCase():
            return "Agent assigned";
        case AGENT_ACTIVITY_ACTION_TYPE['JOIN']?.toLowerCase():
            return "Agent joined";
        case AGENT_ACTIVITY_ACTION_TYPE['LEFT']?.toLowerCase():
            return "Agent left";

        // Default fallback
        default:
            // Try common message fields
            if (actionExecutionData.message) {
                return truncateText(actionExecutionData.message);
            }
            if (actionExecutionData.wa_body_text) {
                return truncateText(actionExecutionData.wa_body_text);
            }
            return "";
    }
};

/**
 * Get formatted last message preview for thread list
 * Returns { senderLabel, messageText } where senderLabel could be "You: ", "Bot: ", "SG: ", or "John: "
 */
export const getFormattedLastMessage = (
    lastMessage: {
        action_type_name: string;
        user_assignment_id: number | null;
        user_first_name?: string | null;
        user_last_name?: string | null;
        thread_last_message_data?: {
            action_execution_data?: Record<string, any>;
        };
    } | null | undefined,
    currentUserAssignmentId: number | null,
    userName?: string | null // User's name from thread metadata
): { senderLabel: string; messageText: string } => {
    if (!lastMessage) {
        return { senderLabel: "", messageText: "NA" };
    }

    const actionTypeName = lastMessage.action_type_name || "";
    const actionExecutionData = lastMessage.thread_last_message_data?.action_execution_data;

    // Get sender info
    const sender = getLastMessageSender(
        actionTypeName,
        actionExecutionData,
        lastMessage.user_assignment_id,
        currentUserAssignmentId,
        lastMessage.user_first_name,
        lastMessage.user_last_name,
        userName
    );

    // Get message text
    const messageText = getLastMessageText(actionTypeName, actionExecutionData);

    // Format sender label - always show for all message types
    let senderLabel = `${sender.label}: `;

    return { senderLabel, messageText: messageText || "Message" };
};

/**
 * Check if the action type is a valid message type
 * @param actionTypeName - The action type from socket data
 * @returns true if it's a valid message action type
 */
export const isValidMessageActionType = (actionTypeName: string): boolean => {
    if (!actionTypeName) return false;
    const normalizedType = actionTypeName.toLowerCase();
    return VALID_MESSAGE_ACTION_TYPES.some(type => type.toLowerCase() === normalizedType);
};

/**
 * Check if a timestamp is within the specified hours threshold
 * @param timestamp - ISO timestamp string
 * @param hours - Number of hours to check against
 * @returns true if timestamp is within the hours threshold
 */
export const isWithinHoursThreshold = (timestamp: string, hours: number): boolean => {
    if (!timestamp) return false;
    try {
        const now = Date.now();
        const time = new Date(timestamp).getTime();
        const diffMs = now - time;
        const thresholdMs = hours * 60 * 60 * 1000;
        return diffMs >= 0 && diffMs <= thresholdMs;
    } catch {
        return false;
    }
};

/**
 * Check if thread meets "Active" conversation criteria
 * A thread is active if flag_archive is false AND at least one of:
 * 1. Has assigned agent (manual_agent_data is not empty)
 * 2. Last activity is within 72 hours
 * 3. Ticket status is "Open"
 * 
 * @param thread - The thread to check
 * @returns true if thread is considered active
 */
export const isThreadActive = (thread: {
    flag_archive?: boolean;
    manual_agent_data?: { user_assignment_id: number }[];
    thread_last_activity_inbox_criteria?: string;
    thread_status?: { ticket_status_type_name?: string } | null;
    thread_ticket_data?: threadTickets[]
}): boolean => {
    // Must not be archived
    if (thread.flag_archive === true) {
        return false;
    }

    // Check if any agent is assigned
    const hasAssignedAgent = Array.isArray(thread.manual_agent_data) && thread.manual_agent_data.length > 0;

    // Check if last activity is within 72 hours
    const isRecentActivity = isWithinHoursThreshold(
        thread.thread_last_activity_inbox_criteria || "",
        ACTIVE_THREAD_HOURS_THRESHOLD
    );

    // Check if ticket status is Open
    // const hasOpenTicket = thread.thread_status?.ticket_status_type_name?.toLowerCase() === ACTIVE_TICKET_STATUS.toLowerCase();
    const hasOpenTicket = thread?.thread_ticket_data?.some((ticket) => ticket?.status_assignments?.some((status) => status?.ticket_status_name?.toLowerCase() === ACTIVE_TICKET_STATUS.toLowerCase())) ?? false;

    // Thread is active if at least one condition is met
    return hasAssignedAgent || isRecentActivity || hasOpenTicket;
};

/**
 * Check if thread matches assignment filter
 * @param thread - The thread to check
 * @param filters - Current filter state
 * @param userAssignmentId - Current user's assignment ID
 * @returns true if thread matches assignment filter
 */
export const doesThreadMatchAssignmentFilter = (
    thread: { manual_agent_data?: { user_assignment_id: number }[] },
    filters: { flagAssignedToMe: boolean; flagUnassigned: boolean },
    userAssignmentId: number | null
): boolean => {
    const { flagAssignedToMe, flagUnassigned } = filters;
    const manualAgentData = thread.manual_agent_data || [];
    const hasAgents = manualAgentData.length > 0;

    // If both are selected or neither is selected → no filter applied
    if ((flagAssignedToMe && flagUnassigned) || (!flagAssignedToMe && !flagUnassigned)) {
        return true;
    }

    // If only "Assigned to Me" is selected
    if (flagAssignedToMe && !flagUnassigned) {
        if (!userAssignmentId) return false;
        return manualAgentData.some(agent => agent.user_assignment_id === userAssignmentId);
    }

    // If only "Unassigned" is selected
    if (flagUnassigned && !flagAssignedToMe) {
        return !hasAgents;
    }

    return true;
};

/**
 * Check if thread matches tags filter
 * @param thread - The thread to check
 * @param selectedTagIds - Array of selected tag IDs
 * @returns true if thread matches tags filter (or no tags filter applied)
 */
export const doesThreadMatchTagsFilter = (
    thread: { thread_tags?: { ticket_tag_id: string }[] },
    selectedTagIds: string[]
): boolean => {
    // No tags filter applied
    if (!selectedTagIds || selectedTagIds.length === 0) {
        return true;
    }

    const threadTags = thread.thread_tags || [];
    // Thread must have at least one of the selected tags
    return threadTags.some(tag => selectedTagIds.includes(tag.ticket_tag_id));
};

/**
 * Check if thread matches ticket status filter
 * @param thread - The thread to check
 * @param selectedStatusIds - Array of selected status IDs
 * @returns true if thread matches status filter (or no status filter applied)
 */
export const doesThreadMatchStatusFilter = (
    thread: { thread_status?: { ticket_status_type_id?: string } | null, thread_ticket_data?: threadTickets[] },
    selectedStatusIds: string[]
): boolean => {
    // No status filter applied
    if (!selectedStatusIds || selectedStatusIds.length === 0) {
        return true;
    }

    // const threadStatusId = thread.thread_status?.ticket_status_type_id;
    const ticketStatusIdExist = thread?.thread_ticket_data?.some((ticket) => ticket?.status_assignments?.some((status) => selectedStatusIds.includes(status?.ticket_status_id)))
    // Thread must have one of the selected statuses
    // return threadStatusId ? selectedStatusIds.includes(threadStatusId) : false;
    return ticketStatusIdExist ? ticketStatusIdExist : false;
};

/**
 * Check if thread matches channels filter
 * @param thread - The thread to check
 * @param selectedChannelIds - Array of selected channel IDs
 * @returns true if thread matches channels filter (or no channels filter applied)
 */
export const doesThreadMatchChannelsFilter = (
    thread: { channel_id?: number },
    selectedChannelIds: string[]
): boolean => {
    // No channels filter applied
    if (!selectedChannelIds || selectedChannelIds.length === 0) {
        return true;
    }

    const threadChannelId = thread.channel_id?.toString();
    return threadChannelId ? selectedChannelIds.includes(threadChannelId) : false;
}; 

/**
 * Check if thread matches last activity filter
 * @param thread - The thread to check
 * @param lastActivityFilter - The last activity filter value
 * @returns true if thread matches last activity filter
 */
export const doesThreadMatchLastActivityFilter = (
    thread: { thread_last_activity_inbox_criteria?: string },
    lastActivityFilter: "all" | "24h" | "3d" | "7d"
): boolean => {
    // No filter or "all" - show everything
    if (!lastActivityFilter || lastActivityFilter === "all") {
        return true;
    }

    const timestamp = thread.thread_last_activity_inbox_criteria;
    if (!timestamp) return false;

    const hoursMap: Record<string, number> = {
        "24h": 24,
        "3d": 72,
        "7d": 168,
    };

    const hours = hoursMap[lastActivityFilter];
    return hours ? isWithinHoursThreshold(timestamp, hours) : true;
}; 

/**
 * Check if thread matches archive filter (for "All" conversation type)
 * @param thread - The thread to check
 * @param filters - Current filter state
 * @returns true if thread matches archive filter
 */
export const doesThreadMatchArchiveFilter = (
    thread: { flag_archive?: boolean },
    filters: { conversationType: "Active" | "All"; flagArchived: boolean; flagUnarchived: boolean }
): boolean => {
    // For "Active" type, only show non-archived
    if (filters.conversationType === "Active") {
        return thread.flag_archive !== true;
    }

    // For "All" type, check archive filters
    const { flagArchived, flagUnarchived } = filters;
    const isArchived = thread.flag_archive === true;

    // If both or neither selected, show all
    if ((flagArchived && flagUnarchived) || (!flagArchived && !flagUnarchived)) {
        return true;
    }

    // If only archived selected, show only archived
    if (flagArchived && !flagUnarchived) {
        return isArchived;
    }

    // If only unarchived selected, show only unarchived
    if (flagUnarchived && !flagArchived) {
        return !isArchived;
    }

    return true;
}; 

/**
 * Check if thread matches search query
 * Searches in: display name, phone number, and last message text
 * @param thread - The thread to check
 * @param searchQuery - The search query string
 * @returns true if thread matches search query (or no search query)
 */
export const doesThreadMatchSearchFilter = (
    thread: {
        thread_metadata?: { metadata_type_name: string; metadata_value: string }[];
        last_message?: {
            thread_last_message_data?: {
                action_execution_data?: {
                    message?: string;
                    wa_body_text?: string;
                    [key: string]: any;
                };
            };
        } | null;
    },
    searchQuery: string
): boolean => {
    // No search query means all threads match
    if (!searchQuery || searchQuery.trim().length === 0) {
        return true;
    }

    const searchTerm = searchQuery.trim().toLowerCase();

    // Get display name from metadata
    const displayName = getThreadDisplayName(thread.thread_metadata || []);
    if (displayName.toLowerCase().includes(searchTerm)) {
        return true;
    }

    // Get phone number from metadata
    const phoneNumber = getThreadPhoneNumber(thread.thread_metadata || []);
    if (phoneNumber.toLowerCase().includes(searchTerm)) {
        return true;
    }

    // Check last message text
    if (thread.last_message?.thread_last_message_data?.action_execution_data) {
        const messageText = thread.last_message.thread_last_message_data.action_execution_data.message ||
            thread.last_message.thread_last_message_data.action_execution_data.wa_body_text ||
            "";
        if (messageText.toLowerCase().includes(searchTerm)) {
            return true;
        }
    }

    return false;
}; 

/**
 * Check if thread matches all current filters
 * @param thread - The thread to check
 * @param filters - Current filter state
 * @param userAssignmentId - Current user's assignment ID
 * @returns true if thread matches all filters
 */
export const doesThreadMatchFilters = (
    thread: {
        flag_archive?: boolean;
        manual_agent_data?: { user_assignment_id: number }[];
        thread_last_activity_inbox_criteria?: string;
        thread_status?: { ticket_status_type_id?: string; ticket_status_type_name?: string } | null;
        thread_ticket_data?: threadTickets[],
        thread_tags?: { ticket_tag_id: string }[];
        channel_id?: number;
        thread_metadata?: { metadata_type_name: string; metadata_value: string }[];
        last_message?: {
            thread_last_message_data?: {
                action_execution_data?: {
                    message?: string;
                    wa_body_text?: string;
                    [key: string]: any;
                };
            };
        } | null;
    },
    filters: InboxControlsState,
    userAssignmentId: number | null
): boolean => {
    // Check conversation type (Active vs All)
    if (filters.conversationType === "Active") {
        if (!isThreadActive(thread)) {
            return false;
        }
    }

    // Check archive filter
    if (!doesThreadMatchArchiveFilter(thread, filters)) {
        return false;
    }

    // Check assignment filter
    if (!doesThreadMatchAssignmentFilter(thread, filters, userAssignmentId)) {
        return false;
    }

    // Check tags filter
    if (!doesThreadMatchTagsFilter(thread, filters.tags)) {
        return false;
    }

    // Check status filter
    if (!doesThreadMatchStatusFilter(thread, filters.ticketStatusIds)) {
        return false;
    }

    // Check channels filter
    if (!doesThreadMatchChannelsFilter(thread, filters.channels)) {
        return false;
    }

    // Check last activity filter
    if (!doesThreadMatchLastActivityFilter(thread, filters.lastActivity)) {
        return false;
    }

    // Check search filter
    if (!doesThreadMatchSearchFilter(thread, filters.searchQuery)) {
        return false;
    }

    return true;
}; 

export const isThreadArchived = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => {
    return currentActiveChatbotThread?.flag_archive ?? false;
} 

/**
 * Possible actions for thread visibility
 */
export type ThreadVisibilityAction = "add" | "remove" | "update" | "ignore";

/**
 * Context required for the getThreadVisibilityDecision function
 */
export interface ThreadVisibilityContext {
    /** Current filter state */
    filters: InboxControlsState;
    /** Current user's assignment ID */
    userAssignmentId: number | null;
    /** Whether the thread already exists in the current list */
    threadExistsInList: boolean;
    /** Current page's start_from value */
    startFrom: number;
    /** Action type from the socket message */
    actionTypeName: string;
}

/**
 * Result of the getThreadVisibilityDecision function
 */
export interface ThreadVisibilityResult {
    /** The action to take */
    action: ThreadVisibilityAction;
    /** Reason for the decision (for debugging) */
    reason: string;
}

/**
 * Unified function to determine what action to take for a thread when a socket notification arrives
 * 
 * Logic:
 * 1. If thread EXISTS in list:
 *    - Check if it still matches filters
 *    - If matches: "update" (keep/update the thread)
 *    - If doesn't match: "remove"
 * 
 * 2. If thread DOESN'T EXIST in list:
 *    - Check if on page 1 (only add new threads on page 1)
 *    - Check if valid action type
 *    - Check if matches filters
 *    - If all pass: "add"
 *    - Otherwise: "ignore"
 * 
 * @param thread - The thread data from socket
 * @param context - The decision context
 * @returns Decision result with action and reason
 */
export const getThreadVisibilityDecision = (
    thread: {
        flag_archive?: boolean;
        manual_agent_data?: { user_assignment_id: number }[];
        thread_last_activity_inbox_criteria?: string;
        thread_status?: { ticket_status_type_id?: string; ticket_status_type_name?: string } | null;
        thread_ticket_data?: threadTickets[],
        thread_tags?: { ticket_tag_id: string }[];
        channel_id?: number;
      thread_metadata?: { metadata_type_name: string; metadata_value: string }[];
        last_message?: {
            thread_last_message_data?: {
                action_execution_data?: {
                    message?: string;
                    wa_body_text?: string;
                    [key: string]: any;
                };
            };
        } | null;
    },
    context: ThreadVisibilityContext
): ThreadVisibilityResult => {
    const { filters, userAssignmentId, threadExistsInList, startFrom, actionTypeName } = context;
    // Check if thread matches current filters
    const matchesFilters = doesThreadMatchFilters(thread, filters, userAssignmentId);

    // ========== THREAD EXISTS IN LIST ==========
    if (threadExistsInList) {
        if (matchesFilters) {
            return {
                action: "update",
                reason: "Thread exists and still matches filters - update it",
            };
        } else {
            return {
                action: "remove",
                reason: "Thread exists but no longer matches filters - remove it",
            };
        }
    }

    // ========== THREAD DOESN'T EXIST IN LIST ==========
    // For new threads, check page first
    if (startFrom !== 1) {
        return {
            action: "ignore",
            reason: "Not on page 1 - only add new threads on first page",
        };
    }

    // Check if action type is valid
    if (!isValidMessageActionType(actionTypeName)) {
        return {
            action: "ignore",
            reason: `Action type "${actionTypeName}" is not a valid message type`,
        };
    }

    // Check if matches filters
    if (matchesFilters) {
        return {
            action: "add",
            reason: "New thread matches filters - add to list",
        };
    } else {
        return {
            action: "ignore",
            reason: "New thread does not match filters - ignore",
        };
    }
}; 

export const getIntegratedChannelOptionText = (option: integratedChannel): string => {
    switch (option.channel_name) {
        case "WhatsApp": {
            const integrationDetails = option.integration_detail;
            return `${integrationDetails?.display_phone_number} (${integrationDetails?.waba_name})`;
        }
        case "website":
        case "Website":
            return `${option?.integration_detail?.domain_name ?? ""} ${option?.integration_detail?.chatbot_name ? `(${option?.integration_detail?.chatbot_name})` : '-'}`;
        case "Instagram":
            return `${option?.integration_detail?.instagram_account_name ?? ""} (${option?.integration_detail?.connected_meta_account_name})`;
        case "Facebook":
            return `${option?.integration_detail?.page_name ?? ""} (${option?.integration_detail?.connected_meta_account_name})`;
        case "slack":
            return `${option?.integration_detail?.slack_channel_name ?? ""} (${option?.integration_detail?.slack_team_name})`
        default:
            return "";
    }
}; 

export const formatPhoneNumber = (phoneNumber: string) => {
    const normalizedPhoneNumber = phoneNumber?.startsWith("+") ? phoneNumber : `+${phoneNumber}`;

    const parsedPhoneNumber = parsePhoneNumberFromString(normalizedPhoneNumber);

    return parsedPhoneNumber?.formatInternational() ?? '';
}

// ============ Agent Name Helpers ============

/**
 * Renders agent name with prefix and suffix
 * Returns formatted string with agent's full name or "Anonymous agent" if name is missing
 */
export const renderAgentName = (
    prefixStr: string,
    thread: currentActiveThreadChatbotThreadData,
    suffixStr: string
): string => {
    const fName = thread?.chatbot_action_execution_data?.private_user_first_name;
    const lName = thread?.chatbot_action_execution_data?.private_user_last_name;

    const isNameMissing =
        (fName == null && lName == null) ||
        (fName == null && lName != null && lName.length === 0) ||
        (lName == null && fName != null && fName.length === 0);

    if (isNameMissing) {
        return `${prefixStr} Anonymous agent${suffixStr}.`;
    }

    const fullName = `${fName || ""} ${lName || ""}`.trim();
    return `${prefixStr} ${fullName}${suffixStr}.`;
};

/**
 * Renders agent chip message based on action type
 */
export const renderAgentChips = (thread: currentActiveThreadChatbotThreadData): string => {
    switch (thread.action_type_name) {
        case AGENT_ACTIVITY_ACTION_TYPE['ASSIGN']:
            return renderAgentName(
                "Chat is now assigned to",
                thread,
                ". Automation is paused until the agent leaves"
            );
        case AGENT_ACTIVITY_ACTION_TYPE['JOIN']:
            return renderAgentName("", thread, " has joined conversation");
        case AGENT_ACTIVITY_ACTION_TYPE['LEFT']:
            return renderAgentName("", thread, " has left conversation");
        default:
            return "";
    }
};

// ============ Chat Feedback Helpers ============

/**
 * Gets display label for chat feedback key
 */
export const getChatFeedbackKeys = (key: string): string => {
    return FEEDBACK_KEY_LABELS[key] || "";
};

// ============ Action Button Styling ============

interface ActionButtonColors {
    bgColor: string;
    textColor: string;
}

/**
 * Gets action button colors based on channel source
 * Returns different colors for WhatsApp vs other channels
 */
// export const getActionButtonColors = (channelSource: string | undefined): ActionButtonColors => {
//     const isWhatsApp = channelSource?.toLowerCase() === "whatsapp";

//     return {
//         bgColor: isWhatsApp ? whiteColor : "#6A097D",
//         textColor: isWhatsApp ? blackColor : whiteColor,
//     };
// };

export const getActionButtonColors = (
    channelSource: string | undefined,
    currentThread: currentActiveChatbotThreadTypeNew | null,
    userAssignmentId: string | number | null
): ActionButtonColors => {
    const isWhatsApp = channelSource?.toLowerCase() === "whatsapp";

    // Default colors
    let bgColor = isWhatsApp ? whiteColor : primaryColor;
    let textColor = isWhatsApp ? blackColor : whiteColor;

    const manualOverride = toScreamingSnakeCase(getManualOverrideConfig(currentThread, userAssignmentId));

    textColor = (isWhatsApp || manualOverride?.toLowerCase() == "leave") ? MANUAL_OVERRIDE_BTN_TEXT_COLORS[manualOverride as keyof typeof MANUAL_OVERRIDE_BTN_TEXT_COLORS] : whiteColor;
    bgColor = (!isWhatsApp && manualOverride?.toLowerCase() == "leave") ? whiteColor : bgColor 
    
    return { bgColor, textColor };
};

// ============ Manual Override Helpers ============

type ManualOverrideAction = "Takeover" | "Leave" | "Join" | "";

/**
 * Determines the manual override action available for the current user
 * @param currentThread - The currently active chatbot thread
 * @param userAssignmentId - The current user's assignment ID
 * @returns The action type: "Takeover", "Leave", "Join", or ""
 */
export const getManualOverrideConfig = (
    currentThread: currentActiveChatbotThreadTypeNew | null,
    userAssignmentId: string | number | null
): ManualOverrideAction => {
    if (!currentThread) return "";

    const manualAgentDataList = currentThread.manual_agent_data ?? [];

    const assignedAgent = manualAgentDataList?.find(
        (agentData: manualAgentData) => String(agentData?.user_assignment_id) === String(userAssignmentId)
    );

    if (!assignedAgent) return "Takeover";
    if (assignedAgent.timestamp_joined) return "Leave";
    return "Join";
};

/**
 * Determines if the manual override textfield should be disabled
 * @param currentThread - The currently active chatbot thread
 * @param userAssignmentId - The current user's assignment ID
 * @returns true if the textfield should be disabled
 */
export const getManualOverrideTextfieldDisabled = (
    currentThread: currentActiveChatbotThreadTypeNew | null,
    userAssignmentId: string | number | null
): boolean => {
    if (!currentThread) return true;

    const manualAgentDataList = currentThread.manual_agent_data ?? [];

    const assignedAgent = manualAgentDataList?.find(
        (agentData: manualAgentData) => String(agentData?.user_assignment_id) === String(userAssignmentId)
    );

    if(!assignedAgent && manualAgentDataList?.length > 0) return true;
    if(isThreadArchived(currentThread)) return true;
    if(isChannelNameChronoxAiWebAppOrWebsite(currentThread)) return false;
    return isUserChatExpired(currentThread);

    // if (!assignedAgent) return true;
    // if (assignedAgent.timestamp_joined) {
    //     return isUserChatExpired(currentThread as currentActiveChatbotThreadTypeNew);
    // }
    // return true;
};

// ============ URL Filter Helpers ============

/**
 * Parses filters from URL search params
 */
export const parseFiltersFromUrl = (searchParams: URLSearchParams): Partial<InboxControlsState> => {
    const conversationType = searchParams.get("conversationType") as "Active" | "All" | null;
    // Only include flagUnassigned if it's explicitly set in URL, otherwise let defaults handle it
    const flagUnassignedParam = searchParams.get("flagUnassigned");
    const flagUnassigned = flagUnassignedParam === "true" ? true : flagUnassignedParam === "false" ? false : undefined;
    // Only include flagAssignedToMe if it's explicitly set in URL, otherwise let defaults handle it
    const flagAssignedToMeParam = searchParams.get("flagAssignedToMe");
    const flagAssignedToMe = flagAssignedToMeParam === "true" ? true : flagAssignedToMeParam === "false" ? false : undefined;
    const flagArchived = searchParams.get("flagArchived") === "true";
    const flagUnarchived = searchParams.get("flagUnarchived") === "true";
    const ticketStatusIds = searchParams.get("ticketStatusIds")?.split(",").filter(Boolean) || [];
    const ticketCreatedBy = searchParams.get("ticketCreatedByIds")?.split(",").filter(Boolean) || [];
    const flagTicketUnassigned = searchParams.get("flagTicketUnassigned") === "true";
    const flagTicketAssignedToOther =searchParams.get("flagTicketAssignedToOther") === "true";
    const ticketAssignedTo = searchParams.get("ticketAssignedTo")?.split(",").filter(Boolean) || [];
    const tags = searchParams.get("tags")?.split(",").filter(Boolean) || [];
    const lastActivity = searchParams.get("lastActivity") as "all" | "24h" | "3d" | "7d" | null;
    const searchQuery = searchParams.get("searchQuery");
    const ticketSearchQuery = searchParams.get("ticketSearchQuery");
    const integratedChannelId = searchParams.get("integratedChannelId")?.split(",").filter(Boolean) || [];

    return {
        ...(conversationType && { conversationType }),
        ...(flagUnassigned !== undefined && { flagUnassigned }),
        ...(flagAssignedToMe !== undefined && { flagAssignedToMe }),
        flagArchived,
        flagUnarchived,
        ...(ticketStatusIds.length > 0 && { ticketStatusIds }),
        ...(ticketCreatedBy.length > 0 && { ticketCreatedBy }),
        ...(flagTicketUnassigned !== undefined && { flagTicketUnassigned }),
        ...(flagTicketAssignedToOther !== undefined && { flagTicketAssignedToOther }),
        ...(ticketAssignedTo.length > 0 && { ticketAssignedTo }),
        ...(tags.length > 0 && { tags }),
        ...(lastActivity && { lastActivity }),
        ...(searchQuery && { searchQuery }),
        ...(ticketSearchQuery && { ticketSearchQuery }),
        ...(integratedChannelId && { integratedChannelId }),
    };
};

/**
 * Creates URL search params from filters
 * Returns a function that can be passed to setSearchParams
 */
export const createFilterParams = (currentFilters: InboxControlsState) => {
    return (prevParams: URLSearchParams): URLSearchParams => {
        const newParams = new URLSearchParams(prevParams);
        const chatbotUserId = newParams.get("chatbot_user_id");

        // Clear all filter params
        FILTER_URL_KEYS.forEach((key) => newParams.delete(key));

        // Build filter entries with their URL values
        const filterEntries: [string, string | undefined][] = [
            ["conversationType", currentFilters.conversationType !== DEFAULT_INBOX_CONTROLS_STATE.conversationType ? currentFilters.conversationType : undefined],
            ["flagUnassigned", currentFilters.flagUnassigned ? "true" : undefined],
            ["flagAssignedToMe", currentFilters.flagAssignedToMe ? "true" : undefined],
            ["flagArchived", currentFilters.flagArchived ? "true" : undefined],
            ["flagUnarchived", currentFilters.flagUnarchived ? "true" : undefined],
            ["ticketStatusIds", currentFilters.ticketStatusIds?.length ? currentFilters.ticketStatusIds.join(",") : undefined],
            ["ticketCreatedByIds", currentFilters.ticketCreatedBy?.length ? currentFilters?.ticketCreatedBy?.join(",") : undefined],
            ["flagTicketUnassigned", currentFilters.flagTicketUnassigned ? "true" : undefined],
            ["flagTicketAssignedToOther", currentFilters.flagTicketAssignedToOther ? "true" : undefined],
            ["ticketAssignedTo", currentFilters.ticketAssignedTo?.length ? currentFilters.ticketAssignedTo?.join(",") : undefined],
            ["tags", currentFilters.tags?.length ? currentFilters.tags.join(",") : undefined],
            ["lastActivity", currentFilters.lastActivity !== DEFAULT_INBOX_CONTROLS_STATE.lastActivity ? currentFilters.lastActivity : undefined],
            ["searchQuery", currentFilters.searchQuery ? currentFilters.searchQuery : undefined],
            ["ticketSearchQuery", currentFilters.ticketSearchQuery ? currentFilters.ticketSearchQuery : undefined],
            ["integratedChannelId", currentFilters.integratedChannelId?.length ? currentFilters.integratedChannelId?.join(",") : undefined],
        ];

        // Set only non-undefined values
        filterEntries.forEach(([key, value]) => value && newParams.set(key, value));

        // Reset pagination when filters change
        newParams.delete("page");

        // Preserve chatbot_user_id
        if (chatbotUserId) newParams.set("chatbot_user_id", chatbotUserId);

        return newParams;
    };
};

/**
 * Creates a setSearchParams updater that sets or clears chatbot_user_id.
 */
export const updateChatbotUserIdParam = (chatbotUserId: string | null) => {
    return (prevParams: URLSearchParams): URLSearchParams => {
        const newParams = new URLSearchParams(prevParams);
        if (chatbotUserId) {
            newParams.set("chatbot_user_id", chatbotUserId);
        } else {
            newParams.delete("chatbot_user_id");
        }
        return newParams;
    };
};

type threadDataKey = 
    | "metaIntegrationWhatsAppId"
    | "userWAPhoneNumber"
    | "integratedChannelId"

export const isWhatsAppIntegration = (integration: metaIntegrationWhatsAppDataNew | metaIntegrationFaceBookDataNew | metIntegrationInstagramDataNew): integration is metaIntegrationWhatsAppDataNew => integration.channel_name.toLowerCase() == 'whatsapp';
export const isInstagramIntegration = (integration: metaIntegrationWhatsAppDataNew | metaIntegrationFaceBookDataNew | metIntegrationInstagramDataNew): integration is metIntegrationInstagramDataNew => integration.channel_name.toLowerCase() === "instagram";

export const findWhatsAppIntegration = (integrationListNewStructure: (metaIntegrationWhatsAppDataNew | metaIntegrationFaceBookDataNew | metIntegrationInstagramDataNew)[], integratedChannelId: string | number) => {
    if(!integratedChannelId) return null;

    return integrationListNewStructure?.filter((integration) => isWhatsAppIntegration(integration))
            ?.find((integration) => String(integration?.integrated_channel_id) === String(integratedChannelId)) ?? null;
}

export const getThreadData = (dataKey: threadDataKey, currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null, newThreadInfo: newThreadInfo | null, integrationListNewStructure: (metaIntegrationWhatsAppDataNew | metaIntegrationFaceBookDataNew | metIntegrationInstagramDataNew)[] | null) => {
    const { flagRenderNewConversation } = useRuleBasedInboxStore.getState();

    switch(dataKey){
        case 'integratedChannelId':
            if(flagRenderNewConversation) return newThreadInfo?.integrated_channel_id;
            else return currentActiveChatbotThread?.integrated_channel_id ?? 0;
        case 'userWAPhoneNumber':
            if(flagRenderNewConversation) {
                return newThreadInfo?.user_wa_phone_number;
            } else {
                return getMetadataValue(currentActiveChatbotThread?.thread_metadata ?? [], METADATA_KEYS['PHONE_NUMBER']) ?? '';
            }
        case 'metaIntegrationWhatsAppId':
            if(flagRenderNewConversation){
                return integrationListNewStructure?.filter(isWhatsAppIntegration)?.
                        find((whatsAppIntegration) => whatsAppIntegration?.meta_integration_phone_number_data?.some((phoneNumber) => {
                            return String(phoneNumber?.integrated_channel_id) === newThreadInfo?.integrated_channel_id
                        }))?.meta_integration_whatsapp_id as number;
            } else {
                return Number(currentActiveChatbotThread?.meta_integration_whatsapp_id);
            }
        default: 
            return "";
    }
} 

// Removes Markdown / WhatsApp formatting markers so copied text is plain, readable text.
// - Markers are only removed when they wrap text, so normal text like "snake_case", "john_doe@mail.com",
//   "Order #12", "5 * 3" or "price > 500" is kept as it is.
// - Code, URLs and escaped characters (\*) are copied exactly as written.
// - Links keep their URL: [Pay here](https://...) -> "Pay here (https://...)".
export function stripMarkdown(text: string) {
    if (!text) {
        return "";
    }

    // Parts that must not be touched by the formatting rules are swapped for placeholders and restored at the end
    const protectedParts: string[] = [];
    const protect = (value: string) => `${protectedParts.push(value) - 1}`;

    return text
        // Escaped characters: \* \_ etc. are literal characters, not formatting
        .replace(/\\([\\`*_~#>\[\]()!|-])/g, (_, char: string) => protect(char))
        // Code blocks (optional language on the first line) and inline code: keep the code as written
        .replace(/```(?:[^\n`]*\n)?([\s\S]*?)```/g, (_, code: string) => protect(code))
        .replace(/`([^`\n]+)`/g, (_, code: string) => protect(code))
        // Images: keep the alt text
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
        // Links: keep the text and the URL (just the URL when the text is the URL)
        .replace(/\[([^\]]+)\]\(([^)\s]+)[^)]*\)/g, (_, linkText: string, url: string) =>
            linkText.trim() === url ? protect(url) : `${linkText} (${protect(url)})`)
        // Plain URLs: may contain _ * ~ that are not formatting
        .replace(/\b(?:https?:\/\/|www\.)[^\s<>()]+/g, (url: string) => protect(url))
        // Bold / italic / strikethrough (Markdown *** ** __ * _ ~~ and WhatsApp * _ ~), not inside words
        // and not when the text next to the markers is a space
        .replace(/(^|[^\w*])\*\*\*([^\s*](?:[^\n]*?[^\s*])??)\*\*\*(?![\w*])/g, '$1$2')
        .replace(/(^|[^\w*])\*\*([^\s*](?:[^\n]*?[^\s*])??)\*\*(?![\w*])/g, '$1$2')
        .replace(/(^|[^\w_])__([^\s_](?:[^\n]*?[^\s_])??)__(?![\w_])/g, '$1$2')
        .replace(/(^|[^\w~])~~([^\s~](?:[^\n]*?[^\s~])??)~~(?![\w~])/g, '$1$2')
        .replace(/(^|[^\w*])\*([^\s*](?:[^*\n]*?[^\s*])??)\*(?![\w*])/g, '$1$2')
        .replace(/(^|[^\w_])_([^\s_](?:[^_\n]*?[^\s_])??)_(?![\w_])/g, '$1$2')
        .replace(/(^|[^\w~])~([^\s~](?:[^~\n]*?[^\s~])??)~(?![\w~])/g, '$1$2')
        // Headings and blockquotes: only at the start of a line
        .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
        .replace(/^[ \t]*>[ \t]?/gm, '')
        // Horizontal rules (---, ***, ___)
        .replace(/^[ \t]*([-*_])([ \t]*\1){2,}[ \t]*(?=\r?$)/gm, '')
        .replace(/(\d+)/g, (_, index: string) => protectedParts[Number(index)]);
}

// Text to copy for a user message, or null when the message has no text content (media, voice call,
// interactive/order/media templates). Follows the same action types as renderUserMsg.
export const getUserMessageCopyText = (thread: any): string | null => {
    if (!thread) {
        return null;
    }

    const actionTypeInLowerCase = thread?.action_type_name?.toLowerCase() ?? "";
    let messageThread = thread;

    switch (actionTypeInLowerCase) {
        case ACTION_TYPE_NAME["VOICE_CALL"]?.toLowerCase():
            return null;
            
        case USER_MESSAGE_ACTION_TYPES["MEDIA"]?.toLowerCase():
            return thread?.action_execution_data?.caption ?? null;

        case INSTAGRAM_ACTION_TYPES["IG_DM_TEXT_RECEIVED"]?.toLowerCase():
            return thread?.chatbot_action_execution_data?.message?.trim() || null;

        case INSTAGRAM_ACTION_TYPES["IG_COMMENT_RECEIVED"]?.toLowerCase():
            return thread?.chatbot_action_execution_data?.comment_message?.trim() || null;

        case USER_MESSAGE_ACTION_TYPES["AI_MESSAGE_EXECUTION"]?.toLowerCase():
            messageThread = thread?.action_execution_data;
            break;
    }

    if (!messageThread) {
        return null;
    }

    // Media messages: copy only the caption, if there is one
    if (getMessageType(messageThread) === "media") {
        return getCleanMessageText(messageThread);
    }

    return getCleanMessageText(messageThread);
}

// Text to copy for a bot message, or null when there is nothing to copy (hides the copy icon).
// Rule-based messages follow the same action types as renderBotMsg; AI chatbot messages use the AI execution data.
export const getBotMessageCopyText = (thread: any): string | null => {
    if (!thread) {
        return null;
    }

    const actionTypeInLowerCase = thread?.chatbot_thread_action_type?.toLowerCase() ?? "";

    // Action cards (custom API, save data, tags, contacts, voice call...) have no message text
    if (NON_COPYABLE_BOT_ACTION_TYPES.includes(actionTypeInLowerCase)) {
        return null;
    }

    const actionExecutionData = thread?.chatbot_action_execution_data; 
    switch (actionTypeInLowerCase) {
        case ACTION_TYPE_NAME['SEND_MEDIA']?.toLowerCase():
            return actionExecutionData?.media?.caption ?? null;
        
        case ACTION_TYPE_NAME["TEXT"]?.toLowerCase():
        case INSTAGRAM_ACTION_TYPES['SEND_IG_DM_TEXT']?.toLowerCase():
        case INSTAGRAM_ACTION_TYPES["REPLY_DM_TO_IG_COMMENT"]?.toLowerCase():
            return actionExecutionData?.message ?? null;

        case INSTAGRAM_ACTION_TYPES["REPLY_TO_IG_COMMENT"]?.toLowerCase():
            return actionExecutionData?.comment_message ?? null;

        case ACTION_TYPE_NAME["TEMPLATE"]?.toLowerCase():
            return getWaTemplateCopyText(actionExecutionData);

        // Order details: only the message text, not the order items / amount card
        case ACTION_TYPE_NAME["SEND_ORDER_DETAIL_TEMPLATE"]?.toLowerCase():
            return getTextOrNull(actionExecutionData?.whatsapp_body_text);

        case ACTION_TYPE_NAME["CAROUSEL_TEMPLATE"]?.toLowerCase():
            return getCarouselTemplateCopyText(actionExecutionData);

        case USER_MESSAGE_ACTION_TYPES["AI_MESSAGE_EXECUTION"]?.toLowerCase():
            return getThreadMessageCopyText(actionExecutionData);

        default:
            return getCleanMessageText(thread);
    }
}

// Text of a message rendered by renderThreadMsg, following its message formats:
// media -> caption, interactive / order details (8, 9) -> header (text only), body and footer, media (20) -> caption,
// anything else -> the message text. Media files and buttons are left out.
const getThreadMessageCopyText = (messageThread: any): string | null => {
    if (!messageThread) {
        return null;
    }

    if (getMessageType(messageThread) === "media") {
        return getCleanMessageText(messageThread);
    }

    const threadValue = messageThread?.chatbot_thread_value;
    if (isJsonString(threadValue)) {
        const parsedValue = JSON.parse(threadValue);
        const messageFormatId = Math.trunc(Number(parsedValue?.message_format_id));
        const messageFields = parsedValue?.message_fields;

        if (messageFormatId === 8 || messageFormatId === 9) {
            return getTemplateMessageCopyText(messageFields);
        }
        if (messageFormatId === 20) {
            return getTextOrNull(messageFields?.caption);
        }
    }

    return getCleanMessageText(messageThread);
}

// Text parts of a WhatsApp template (as shown in ChatLogsTemplatePreview): header (only a text header, not a media one),
// body and footer. Media and buttons are left out.
const getWaTemplateCopyText = (templateData: any): string | null => {
    const headerText = templateData?.wa_header_type_name?.toLowerCase() === "text" ? templateData?.wa_header_text : "";
    return joinCopyTextParts([headerText, templateData?.wa_body_text, templateData?.wa_footer_text]);
}

// Text of a carousel template (as shown in CarouselTemplatePreviewInbox): the message above the cards,
// then each card's body and footer. Card media and buttons are left out.
const getCarouselTemplateCopyText = (carouselData: any): string | null => {
    const cardTexts = (carouselData?.wa_carousel_variations ?? []).map((card: any) =>
        joinCopyTextParts([card?.whatsapp_body_text, card?.whatsapp_footer_text])
    );
    return joinCopyTextParts([carouselData?.wa_header_text, ...cardTexts]);
}

// Joins the non-empty text parts with a blank line between them, or null when none have text
const joinCopyTextParts = (textParts: unknown[]): string | null => {
    const nonEmptyParts = textParts.map((text) => getTextOrNull(text)).filter(Boolean);
    return nonEmptyParts.length > 0 ? nonEmptyParts.join("\n\n") : null;
}

// Message text without the source citation markers, or null when there is no text
const getCleanMessageText = (messageThread: any): string | null => {
    return getTextOrNull(getMessage(messageThread));
}

// Also used for a media caption (message_format_id 20): getTextOrNull(message_fields.caption)
export const getTextOrNull = (text: unknown): string | null => {
    return (typeof text === "string" ? text.replace(patternToRemoveSource, "").trim() : "") || null;
}

// Text parts of a WhatsApp interactive / order details message (message_format_id 8 / 9), as shown in its preview:
// header (only a text header, not a media one), body and footer. Buttons are left out.
// Not used for user messages (users don't send templates); kept for the bot message copy feature.
export const getTemplateMessageCopyText = (messageFields: any): string | null => {
    const headerText = messageFields?.header?.type?.toLowerCase() === "text" ? messageFields?.header?.text : "";
    return joinCopyTextParts([headerText, messageFields?.body?.text, messageFields?.footer?.text]);
}

export const getDynamicColorsBySource = (source: string, currentActiveChatbotThread?: currentActiveChatbotThreadTypeOld | currentActiveChatbotThreadTypeNew | null, chatBotDetails?: getChatbotResponseModel | null) => {
    switch (source?.toLowerCase()) {
        case "whatsapp":
            return {
                header_bg_color: '#0C836F',
                header_font_color: '#FFFFFF',
                chatbot_bg_color: '#EFEAE2', //F5F5F5
                agent_chip_bg_color: '#FFFFFF',
                user_msg_bg_color: '#FFFFFF',
                user_msg_bg_color_mobile: "#FFFFFF",
                user_msg_color: "black",
                system_msg_bg_color: "#D9FDD3", //DCF8C9  
                system_msg_bg_color_mobile: "#D9FDD3",
                system_msg_color: "black",
                no_conversation_color: "#000000",
                schedule_send_icon_color: "#000",
                send_msg_bg_color: "#FFFFFF",
                send_msg_icon_color: "#0C836F",
                send_msg_font_color: "black",
            }
        case "facebook":
            return defaultChatThemeColors;
        case "instagram":
            return defaultChatThemeColors;
        default:
            if (currentActiveChatbotThread && "chatbot_theme" in currentActiveChatbotThread) {
                return {
                    header_bg_color: currentActiveChatbotThread.chatbot_theme.chatbot_header_color ?? "#FFFFFF",
                    header_font_color: currentActiveChatbotThread.chatbot_theme.chatbot_header_font_color ?? primaryColor,
                    chatbot_bg_color: currentActiveChatbotThread.chatbot_theme.chatbot_back_ground_color ?? "#FFFFFF",
                    agent_chip_bg_color: '#f0f0f0',
                    user_msg_bg_color: currentActiveChatbotThread.chatbot_theme.user_msg_bg_color ?? tertiaryColor,
                    user_msg_bg_color_mobile: currentActiveChatbotThread.chatbot_theme.user_msg_bg_color ?? "#F4F4F4",
                    user_msg_color: currentActiveChatbotThread.chatbot_theme.user_msg_color ?? "black",
                    system_msg_bg_color: currentActiveChatbotThread.chatbot_theme.system_msg_bg_color ?? "#F4F4F4",
                    system_msg_bg_color_mobile: currentActiveChatbotThread.chatbot_theme.system_msg_bg_color ?? tertiaryColor,
                    system_msg_color: currentActiveChatbotThread.chatbot_theme.system_msg_color ?? "black",
                    no_conversation_color: currentActiveChatbotThread.chatbot_theme.user_msg_bg_color ?? "#000000",
                    schedule_send_icon_color: currentActiveChatbotThread.chatbot_theme.chatbot_header_color ?? "#000",
                    send_msg_bg_color: currentActiveChatbotThread.chatbot_theme.chatbot_send_message_bg_color ?? "#FFFFFF",
                    send_msg_icon_color: currentActiveChatbotThread.chatbot_theme.chatbot_send_message_icon_color ?? primaryColor,
                    send_msg_font_color: currentActiveChatbotThread.chatbot_theme.chatbot_send_message_font_color ?? primaryColor,
                }
            } else if (chatBotDetails && chatBotDetails.chatbot_theme) {
                return {
                    header_bg_color: chatBotDetails.chatbot_theme.chatbot_header_color ?? "#FFFFFF",
                    header_font_color: chatBotDetails.chatbot_theme.chatbot_header_font_color ?? primaryColor,
                    chatbot_bg_color: chatBotDetails.chatbot_theme.chatbot_back_ground_color ?? "#FFFFFF",
                    agent_chip_bg_color: '#f0f0f0',
                    user_msg_bg_color: chatBotDetails.chatbot_theme.user_msg_bg_color ?? tertiaryColor,
                    user_msg_bg_color_mobile: chatBotDetails.chatbot_theme.user_msg_bg_color ?? "#F4F4F4",
                    user_msg_color: chatBotDetails.chatbot_theme.user_msg_color ?? "black",
                    system_msg_bg_color: chatBotDetails.chatbot_theme.system_msg_bg_color ?? "#F4F4F4",
                    system_msg_bg_color_mobile: chatBotDetails.chatbot_theme.system_msg_bg_color ?? tertiaryColor,
                    system_msg_color: chatBotDetails.chatbot_theme.system_msg_color ?? "black",
                    no_conversation_color: chatBotDetails.chatbot_theme.user_msg_bg_color ?? "#000000",
                    schedule_send_icon_color: chatBotDetails.chatbot_theme.chatbot_header_color ?? "#000",
                    send_msg_bg_color: chatBotDetails.chatbot_theme.chatbot_send_message_bg_color ?? "#FFFFFF",
                    send_msg_icon_color: chatBotDetails.chatbot_theme.chatbot_send_message_icon_color ?? primaryColor,
                    send_msg_font_color: chatBotDetails.chatbot_theme.chatbot_send_message_font_color ?? primaryColor,
                }
            } else {
                return defaultChatThemeColors;
            }
    }
} 

export const getMessageType = (thread: any) => {
    if (thread && "media_data" in thread && thread.media_data && thread.media_data.length > 0 && thread.media_data[0].aws_storage_id !== "") {
        return "media"
    }
    return "text";
}

export const getFullAgentName = (firstName: string | null, lastName: string | null) => {
    return (!firstName && !lastName) ? "Anonymous" : `${firstName} ${lastName}`;
}

export function getThread(thread: any, currentActiveChatbotThread?: any) {
    if (currentActiveChatbotThread) {
        let index = currentActiveChatbotThread?.chatbot_thread_data?.findIndex((threadData: any) => threadData?.chatbot_thread_id === (thread?.chatbot_thread_id))
        return currentActiveChatbotThread?.chatbot_thread_data[index + 1]
    }
}

export const getDefaultSendTemplatePayload = (user_thread_id: string, user_wa_phone_number: string, integrated_channel_id: string) => {
    return {
        whatsapp_template_id: 0,
        user_thread_id: user_thread_id,
        user_wa_phone_number: user_wa_phone_number,
        integrated_channel_id: integrated_channel_id,
        template_variable_data: [],
    }
} 

export const isAgentInfoActionType = (action_type_name: string) => {
    return agentInfoActionTypes?.includes(action_type_name?.toLowerCase());
}

export const isLastMsgOlderThan24Hours = (timestamp: string) => {
    const givenTime = new Date(timestamp).getTime();
    const currentTime = Date.now();

    const diff = currentTime - givenTime;
    const hoursInMs = 24 * 60 * 60 * 1000;

    return diff > hoursInMs;
} 

export const isUserChatExpired = (currentActiveChatbotThread: currentActiveChatbotThreadTypeOld | currentActiveChatbotThreadTypeNew | null) => {
    if (currentActiveChatbotThread) {
        const userMessages = currentActiveChatbotThread?.chatbot_thread_data?.filter((thread) => thread?.flag_user_message);
        let isLastUserMessageExpired = userMessages.length == 0 ? true : isLastMsgOlderThan24Hours(userMessages?.[userMessages?.length - 1]?.timestamp);

        return isLastUserMessageExpired;
    }
    return true;
} 

export const isLastMarketingTemplateOlderThan24Hours = (currentActiveChatbotThread: any) => {
    if (currentActiveChatbotThread) {
        const botMessages = currentActiveChatbotThread?.chatbot_thread_data?.filter((thread: any) => (!(thread.flag_user_message) && !isAgentInfoActionType(thread?.action_type_name ?? '')));
        const lastMarketingBotMessage = botMessages.filter((message: any) => (
            message?.chatbot_action_execution_data?.wa_template_category_name && message?.chatbot_action_execution_data?.wa_template_category_name?.toLowerCase() == "marketing"
        ));
        const isLastBotMarketingMessageExpired = botMessages.length == 0 ? true : lastMarketingBotMessage.length == 0 ? true : isLastMsgOlderThan24Hours(lastMarketingBotMessage?.[lastMarketingBotMessage?.length - 1]?.timestamp);

        return isLastBotMarketingMessageExpired;
    }
    return true;
}

export const getTimeDifferences = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);

    const diffMS = end.getTime() - start.getTime();
    if (diffMS < 1000) {
        return `${diffMS} ms`;
    } else {
        const seconds = (diffMS / 1000).toFixed(3);
        return `${seconds} s`;
    }
} 

export const getReleventWhatsappStatus = (thread: any) => {
    return thread?.chatbot_action_execution_data?.whatsapp_message_status || thread?.whatsapp_message_status || thread?.thread_template_data?.action_execution_data?.whatsapp_message_status || [];
}

export const getAzureThreadLink = (openai_assistant_id: string) => {
    return `https://ai.azure.com/resource/assistants?wsid=/subscriptions/abafcf46-5211-4abe-a82c-6497c287629c/resourceGroups/writewiz-test/providers/Microsoft.CognitiveServices/accounts/chronoxAI-chat-assistant/projects/process-x-data-migration&tid=93c2b876-8e5f-4343-91ac-c56505d85a12&assistantId=${openai_assistant_id}`;
}

export const getOpenAiThreadLink = (openai_assistant_id: string, openai_thread_id: string) => {
    return `https://platform.openai.com/playground/assistants?assistant=${openai_assistant_id}&thread=${openai_thread_id}`;
}

export const getEmptyThreadNoteObj = (userDetail: UserMeResponseDataModel) => {
    return {
        thread_note_id: "",
        thread_note_value: "",
        thread_note_timestamp: new Date().toISOString(),
        performed_by_email: userDetail?.user_email ?? "",
        performed_by_name: `${userDetail?.user_first_name ?? ""} ${userDetail?.user_last_name ?? ""}`,
        flag_editable: true,
        updated_thread_note_value: "",
    }
}

// Extract the media file name from the URL
export const extractFileNameFromUrl = (url: string) => {
    if(url){
        return url?.replace(/%2F/gi, '/')?.split('/')?.pop() ?? ''
    }
    return '';
} 

export const getMediaUrl = (vendorData: vendorDataResponse | null, storageId: string | number) => {
    return `${getVendorField(vendorData, "backend api", process.env.REACT_APP_BASE_URL)}/api/storage/file?file_id=${storageId}&flag_info=false`;
}

export const checkIfUserIsAtBottomInChatComponent = () => {
    const container = document.getElementById("chatUI");

    let isAtBottom = false;
    if (container) {
        isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;// threshold
    }

    return isAtBottom;
} 

type NormalizedValue = string | NormalizedArray | NormalizedObject;
interface NormalizedArray extends Array<NormalizedValue> {}
interface NormalizedObject {
    [key: string]: NormalizedValue;
}

export const normalizeValuesToStrings = (input: any): NormalizedValue => {

    const typeOfInput = typeof input;

    switch (typeOfInput) {
        case 'object':
            if (input === null) {
                return String(input);
            }

            if (Array.isArray(input)) {
                if (input.length === 0) {
                    return getValueOrDefault(null);
                }
                return input.map((item: any) => normalizeValuesToStrings(item));
            }

            const result: NormalizedObject = {};
            for (const key in input) {
                if (Object.prototype.hasOwnProperty.call(input, key)) {
                    result[key] = normalizeValuesToStrings(input[key]);
                }
            }
            return result;

        case 'string':
            try {
                const parsed = JSON.parse(input);
                if(parsed !== null && typeof parsed === 'object') {
                    return normalizeValuesToStrings(parsed);
                }
            } catch {
                // Not a JSON string, continue
            }

            return getValueOrDefault(String(input));
        default:
            return getValueOrDefault(String(input));
    }
}; 

export const getNewEmptyField = (field: ticketFieldData) => {
    return {
        thread_ticket_field_id: field?.thread_ticket_field_id,
        field_value: '',
        thread_ticket_field_data_id: generateUUID()
    }
} 

export const getNewEmptyStatusObj = (status: ticketStatusData) => {
    return {
        ticket_status_id: status?.ticket_status_id,
        thread_ticket_status_assignment_id: '',
    }
} 

export const getnewEmptyAgentAssignmentObj = (agent: organizationUserDataModel) => {
    return {
        agent_user_assignment_id: agent?.user_assignment_id,
        thread_ticket_agent_assignment_id: ''
    }
} 

export const getTicketPayload = (chatbotUserId: string | null, ticketFieldData: ticketFieldData[] | null, selectedTicket: threadTickets | null, ticketStatusData: ticketStatusData[] | null) => {

    const notFilledTicketField = ticketFieldData?.filter((field) => !selectedTicket?.field_data?.find((fieldData) => fieldData?.thread_ticket_field_id === field?.thread_ticket_field_id))?.map((field) => {
        return getNewEmptyField(field);
    }) || [];
    const openStatus = selectedTicket ? null : ticketStatusData?.find((status) => !status?.flag_substatus && status?.ticket_status_name === THREAD_STATUS['OPEN'])

    return {
        thread_ticket_id: selectedTicket?.thread_ticket_id ?? '',
        thread_id: chatbotUserId ?? '',
        field_data: selectedTicket ? [...selectedTicket?.field_data, ...notFilledTicketField] : ticketFieldData?.map((field) => {
            return getNewEmptyField(field)
        }),
        agent_assignments: selectedTicket ? selectedTicket?.agent_assignments : [],
        status_assignments: selectedTicket ? selectedTicket?.status_assignments : [openStatus]
    }
} 

export const getUserAgentOptions = (organizationDetails: getOrganizationDetailDataModel[] | null, userAssignmentRoleData: userAssignmentRoleDataModel[] | null) => {
    return organizationDetails?.[0].organization_user_data?.filter((user) => {
        if(isWaSmbUser(user?.private_user_email)) return false;

        if(user?.user_organization_role_name?.toLowerCase() === ORGANIZATION_ROLES['AGENT_ROLE']?.toLowerCase()){  
            return true;
        }

        const isNewRoleIsAgent = userAssignmentRoleData?.find((role) => role?.user_assignment_id === user?.user_assignment_id && role?.chronox_user_role_name?.toLowerCase() === USER_ASSIGNMENT_ROLES['AGENT_ROLE']?.toLowerCase());
        if(isNewRoleIsAgent) return true;

        return false;
    });
} 

export const hasAccessToAssignAgent = (userDetail: UserMeResponseDataModel) => {
    const effectiveUserRoleName = getEffectiveUserRoleName(userDetail);
    const allowedRoles = [
        ORGANIZATION_ROLES['ADMIN_ROLE']?.toLowerCase(),
        ORGANIZATION_ROLES['OWNER_ROLE']?.toLowerCase(),
        USER_ASSIGNMENT_ROLES['ADMIN_ROLE']?.toLowerCase(),
        USER_ASSIGNMENT_ROLES['AGENT_ADMIN_ROLE']?.toLowerCase(),
        USER_ASSIGNMENT_ROLES['OWNER_ROLE']?.toLowerCase()
    ]
    if(isAdmin(userDetail) || allowedRoles?.includes(effectiveUserRoleName)){
        return true;
    }

    return false;
} 

export const hasAssignedAgent = (currentActiveChatbotThread: currentActiveChatbotThreadTypeOld | currentActiveChatbotThreadTypeNew | null) => {
    if(currentActiveChatbotThread && "manual_agent_data" in currentActiveChatbotThread && currentActiveChatbotThread?.manual_agent_data?.length > 0){
        return true;
    }
    return false;
}

export const isOnlySmbUserAssigned = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => {
    if(hasAssignedAgent(currentActiveChatbotThread) && currentActiveChatbotThread){
        const manualAgentData = currentActiveChatbotThread?.manual_agent_data ?? [];
        if(manualAgentData?.length === 1 && isWaSmbUser(manualAgentData?.[0]?.private_user_first_name)){
            return true;
        }
    }
    return false;
} 

export const shouldShowAssignAgentOption = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => {
    if(currentActiveChatbotThread && !isChannelNameChronoxAiWebApp(currentActiveChatbotThread)){
        const manualAgentData = currentActiveChatbotThread?.manual_agent_data ?? [];
        if(manualAgentData?.length === 0 || isOnlySmbUserAssigned(currentActiveChatbotThread)){
            return true;
        }
    }
    return false;                                                             
}

export const isLoggedInUserAgent = (assignedAgentId: number | string, loggedInUserId: number | string) => {
    return String(assignedAgentId) === String(loggedInUserId);
} 

export const isLoggedInUserAssigned = (userDetail: UserMeResponseDataModel, currentActiveChatbotThread: currentActiveChatbotThreadTypeNew) => {
    return currentActiveChatbotThread?.manual_agent_data?.some((agent) => isLoggedInUserAgent(agent?.user_assignment_id, userDetail?.user_assignment_id))
} 

export const getAgentActionSuccessMsg = (action: string, assignedAgentId: string | number, loggedInUserId: string | number) => {
    let successMsg = '';
    switch(action){
        case 'assign':
            if(isLoggedInUserAgent(assignedAgentId, loggedInUserId)){
                successMsg = 'You have been assigned as an agent successfully.';
            } else {
                successMsg = 'Agent assigned successfully.';
            }
            break;
        case 'join':
            if(isLoggedInUserAgent(assignedAgentId, loggedInUserId)){
                successMsg = 'You joined this conversation.';
            } else {
                successMsg = 'Agent joined this conversation.';
            }
            break;
        case 'leave': 
            if(isLoggedInUserAgent(assignedAgentId, loggedInUserId)){
                successMsg = 'You have been removed as an agent.';
            } else {
                successMsg = 'Agent Removed Successfully.';
            }
            break;
        default: 
            successMsg = '';
    }
    return successMsg;
} 

export const getAgentName = (agent: ThreadManualAgentData | manualAgentData) => {
    if (agent?.private_user_first_name || agent?.private_user_last_name) {
        return `${agent?.private_user_first_name} ${agent?.private_user_last_name ?? ''}`.trim();
    }
    return "Anonymous agent";
} 

export const getAgentNameInitials = (agent: ThreadManualAgentData | manualAgentData) => {
    const firstName = agent?.private_user_first_name || "";
    const lastName = agent?.private_user_last_name || "";

    if (firstName && lastName) {
        return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
        return firstName[0].toUpperCase();
    } else if (lastName) {
        return lastName[0].toUpperCase();
    }
    return "A";
}

export const isConversationHandleByOtherAgent = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null, userAssignmentId: number) => {
    return Boolean(hasAssignedAgent(currentActiveChatbotThread) && currentActiveChatbotThread && (String(currentActiveChatbotThread?.manual_agent_data?.[0]?.user_assignment_id) !== String(userAssignmentId)))
}

export const isChannelNameChronoxAiWebApp = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => {
    const allowedChannelNames = [
        THREAD_CHANNEL_NAMES['CHRNOX_AI_WEB_APP']?.toLowerCase(),
        THREAD_CHANNEL_NAMES['CHRONOX_AI_WEB_APP']?.toLowerCase()
    ]
    return Boolean(currentActiveChatbotThread && allowedChannelNames?.includes(currentActiveChatbotThread?.channel_name?.toLowerCase()));
} 

export const isChannelNameChronoxAiWebAppOrWebsite = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => {
    return Boolean(currentActiveChatbotThread && isChannelNameWebsiteOrChronox(currentActiveChatbotThread?.channel_name?.toLowerCase()));
} 

export const isChannelNameWhatsApp = (currentActiveChatbotThread: currentActiveChatbotThreadTypeNew | null) => {
    return Boolean(currentActiveChatbotThread && currentActiveChatbotThread?.channel_name?.toLowerCase() === THREAD_CHANNEL_NAMES['WHATSAPP']?.toLowerCase());
}

export const scrollToBottomInChat = () => {
    const container = document.getElementById("chatUI");
    if (container) {
        setTimeout(() => {
            container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
        }, 100);
    }
}

export const sortUserThreadByLastMessageTime = (threads: (closedChats | whatsAppChatsData)[]) => {
    return threads?.sort((a: (closedChats | whatsAppChatsData), b: (closedChats | whatsAppChatsData)) => {
        const timeA = moment(a.last_message_time).format('YYYY-MM-DD HH:mm:ss');
        const timeB = moment(b.last_message_time).format('YYYY-MM-DD HH:mm:ss');
        return timeB.localeCompare(timeA)
    });
} 

export const doesAllowToShowThreadMessage = (data: currentActiveThreadChatbotThreadData) => {
    if(!data) return false; 
    
    return (data?.action_execution_data || data?.action_type_name?.toLowerCase() == CHATBOT_ACTION_TYPE['CLOSED']?.toLowerCase() || data?.action_type_name?.toLowerCase() == CHATBOT_ACTION_TYPE['DEPLOYED']?.toLowerCase());
} 

export const sortThreadMessageByTimeStamp = (threadData: currentActiveThreadChatbotThreadData[]) => {
    return threadData?.sort((a: currentActiveThreadChatbotThreadData, b: currentActiveThreadChatbotThreadData) =>  getThreadMessageTimeMs(a) - getThreadMessageTimeMs(b));
} 

export const isUserMessage = (action_type_name: string, actionExecutionData: chatbot_action_execution_data) => {
    const isUserMessageActionType = Boolean(action_type_name && userMessagesActionTypes?.includes(action_type_name?.toLowerCase() ?? ""));
    const isIncomingVoiceCallAction = Boolean(action_type_name && action_type_name?.toLowerCase() == ACTION_TYPE_NAME["VOICE_CALL"]?.toLowerCase() && actionExecutionData?.flag_voice_direction_inbound);
    const isAIChatbotThread = Boolean(action_type_name && action_type_name?.toLowerCase() === USER_MESSAGE_ACTION_TYPES['AI_MESSAGE_EXECUTION']?.toLowerCase() && actionExecutionData?.flag_user_message);

    return isUserMessageActionType || isIncomingVoiceCallAction || isAIChatbotThread;
} 

export const isDefaultActionType = (action_type_name: string) => {
    return DEFAULT_ACTION_TYPES?.includes(action_type_name?.toLowerCase() ?? "");
} 

export const isHideChatActionMenuItem = (item: { title: string }, userDetail: UserMeResponseDataModel, apiRoutesPermissionAssignment: apiRoutesPermissionAssignmentsDataModel[] | null) => {
    const { currentActiveChatbotThread } = useRuleBasedInboxStore.getState();

    if(!currentActiveChatbotThread) return false;

    const menuItemName = item.title?.toLowerCase();

    if(!hasAccessToAssignAgent(userDetail) && (menuItemName == "assign agent" || menuItemName == "remove agent")){
        return true;
    }

    if(!isAdmin(userDetail) && menuItemName == "delete"){
        return true;
    }

    if((menuItemName == "close" || menuItemName == "assign agent" || menuItemName == "remove agent") && !hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail)){
        return true;
    }

    if(menuItemName == "reset" && !hasAIChatbotPageAccess(apiRoutesPermissionAssignment, userDetail)){
        return true;
    }

    if(menuItemName == "delete" && !hasInboxAdminAccess(apiRoutesPermissionAssignment, userDetail)){
        return true;
    }

    return false;
} 

export const replaceCannedResponseVariables = (message_content: string, channel_name: string, metaData: threadMetaData[], userDetail: UserMeResponseDataModel) => {
    const keyMap: Record<string, string> = {
        name: METADATA_KEYS['NAME'],
        phone_number: METADATA_KEYS['PHONE_NUMBER'],
    };

    let content = message_content || "";
    const { user_first_name, user_last_name, user_email } = userDetail;

    CANNED_RESPONSE_VARIABLES.forEach(({ key }) => {
        
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
        const threadKey = keyMap[key];
        const replacement = key === "agent_name" ? `${(!user_first_name && !user_last_name) ? user_email : `${user_first_name} ${user_last_name}`}` : getMetadataValue(metaData, threadKey) || "";
        content = content.replace(regex, replacement);
    });
    return content;
} 

export const sendCannedResMsg = async (currentCannedRes: CannedResponseTemplate | null, handleSendMessageManually: (inputValue: string, flagMediaMessage: boolean, flagCannedResponseTemplate: boolean, flagTemplateMessage: boolean, selectedMediaSet: uploadedMedia[], sendTemplatePayload: sendTemplatePayload | null, cannedResponsePayload?: sendCannedResponsePayload) => Promise<void>, userDetail: UserMeResponseDataModel) => {
    // const currentActiveChatbotThread = get().currentActiveChatbotThread;
    const { currentActiveChatbotThread } = useRuleBasedInboxStore.getState();

    if(currentActiveChatbotThread){
        const payload: sendCannedResponsePayload = {
            canned_response_template_id: currentCannedRes?.canned_response_template_id || "", 
            canned_response_messages: currentCannedRes?.canned_response_messages?.map((msg) => {
                return {
                    message_type: msg?.content_type?.name?.toLowerCase() == "text" ? "text" : "media",
                    text: msg?.content_type?.name?.toLowerCase() == "text" ? replaceCannedResponseVariables(msg?.message_content, currentActiveChatbotThread?.channel_name, currentActiveChatbotThread?.thread_metadata, userDetail) : null,
                    thread_id: currentActiveChatbotThread?.thread_id,
                    end_user_phone_number: getMetadataValue(currentActiveChatbotThread?.thread_metadata, METADATA_KEYS['PHONE_NUMBER']) || "", 
                    media: msg?.content_type?.name?.toLowerCase() == "text" ? null : [
                        {
                            type: msg?.content_type?.name?.toLowerCase() || "",
                            url: msg?.message_url, 
                            caption: replaceCannedResponseVariables(msg?.message_content, currentActiveChatbotThread?.channel_name, currentActiveChatbotThread?.thread_metadata, userDetail),
                        }
                    ]
                }
            }) || [],
        }

        await handleSendMessageManually("", false, true, false, [], null, payload);
    }
} 

export const shouldShowCreateTicketOption = (apiRoutesPermissionAssignment: apiRoutesPermissionAssignmentsDataModel[] | null, userDetail: UserMeResponseDataModel) => {
    const { currentActiveChatbotThread } = useRuleBasedInboxStore.getState();
    if (currentActiveChatbotThread && hasInboxReadAccess(apiRoutesPermissionAssignment, userDetail)) {
        return true;
    }
    return false;
} 

// Sort threads by last message time (newest first)
export const sortThreads = (threads: RuleBasedThread[]): RuleBasedThread[] => {
    return [...threads].sort((a, b) => {
        const timeA = new Date(a.thread_last_activity_inbox_criteria || 0).getTime();
        const timeB = new Date(b.thread_last_activity_inbox_criteria || 0).getTime();
        return timeB - timeA;
    });
}

export interface MapThreadExecutionOptions {
    channelName: string;
    flagViewDetailedLog?: boolean;
    flagNeedToShowDetailedLogBtn?: boolean;
}

/**
 * Maps a single thread_execution_data item into the chatbot_thread_data shape
 * used by the inbox chat view (REST load + socket updates).
 */
export const mapThreadExecutionItem = (
    chatbot: any,
    {
        channelName,
        flagViewDetailedLog = false,
        flagNeedToShowDetailedLogBtn = false,
    }: MapThreadExecutionOptions
) => ({
    ...chatbot,
    chatbot_thread_id: chatbot.thread_execution_log_id,
    flag_user_message: isUserMessage(chatbot.action_type_name, chatbot?.action_execution_data),
    chatbot_route_source: channelName,
    chatbot_thread_action_type: chatbot.action_type_name,
    chatbot_thread_value: chatbot.action_execution_data?.message || "",
    chatbot_action_execution_data: chatbot.action_execution_data || {},
    timestamp: chatbot.step_end_timestamp ? chatbot.step_end_timestamp : chatbot.step_start_timestamp,
    flag_show_action:
        !flagViewDetailedLog && flagNeedToShowDetailedLogBtn
            ? isDefaultActionType(chatbot?.action_type_name ?? "")
            : true,
    chatbot_user_active_flag: true,
});

/**
 * Filters, optionally sorts, and maps thread_execution_data → chatbot_thread_data.
 */
export const mapThreadExecutionToChatMessages = (
    executions: any[] | null | undefined,
    options: MapThreadExecutionOptions
) => {
    const filtered = (executions || []).filter((data) => doesAllowToShowThreadMessage(data));

    return (sortThreadMessageByTimeStamp(filtered)).map((chatbot: any) => mapThreadExecutionItem(chatbot, options));
};

/**
 * Move `threadId` to the end of the Recent group without re-sorting other threads by timestamp.
 * Used only for socket-driven list updates so the updated thread sits below existing Recent threads.
 */
export const placeThreadAtBottomOfRecentGroup = (
    threads: RuleBasedThread[],
    threadId: string,
    recentThreadIds: Record<string, boolean>
): RuleBasedThread[] => {
    const threadIndex = threads.findIndex((t) => String(t.thread_id) === String(threadId));
    if (threadIndex === -1) return threads;

    const thread = threads[threadIndex];
    const withoutThread = threads.filter((t) => String(t.thread_id) !== String(threadId));

    const isRecentThread = (t: RuleBasedThread) =>
        recentThreadIds[t.thread_id] === true ||
        recentThreadIds[String(t.thread_id)] === true ||
        isWithinRecentThreshold(t.thread_last_activity_inbox_criteria);

    // Preserve relative order within each group; put the updated thread after all other Recent threads
    const recentOthers: RuleBasedThread[] = [];
    const nonRecentOthers: RuleBasedThread[] = [];
    withoutThread.forEach((t) => {
        if (isRecentThread(t)) {
            recentOthers.push(t);
        } else {
            nonRecentOthers.push(t);
        }
    });

    return [...recentOthers, thread, ...nonRecentOthers];
};

export const isInteractiveOrderDetailsMsg = (thread: ThreadMsgFields | { chatbot_thread_value?: string | null } | null | undefined) => {
    const threadValue = thread?.chatbot_thread_value;

    if (threadValue) {
        try {
            const json = JSON.parse(threadValue);
            if (
                json?.type?.toLowerCase() === "interactive_order_details_payment" &&
                json?.payload?.type?.toLowerCase() === "order_details"
            ) {
                return {
                    status: true,
                    jsonObj: json,
                };
            }
        } catch {
            return {
                status: false,
            };
        }
    }
    return {
        status: false,
    };
};

export const isCatalogOrderMessage = (thread: any) => {
    const isMessageTypeOrder = thread?.action_execution_data?.message_type?.toLowerCase() === "order";
    if (isMessageTypeOrder) { // Static Chatbot's Catalog Order
        const productItems = thread?.action_execution_data?.payload_data?.find(
            (payload: ChatbotActionPayloadItem) => payload?.key?.toLowerCase() === "product_items"
        )?.value;
        if (productItems) {
            try {
                const productItemsJson = JSON.parse(String(productItems));
                return {
                    status: true,
                    jsonObj: productItemsJson,
                };
            } catch {
                return { status: false };
            }
        }
        return {
            status: true,
        };
    }
    return {
        status: false,
    };
};

export const isCatalogIdAvailableInThread = (thread: any) => {
    const threadValue = thread?.chatbot_thread_value;
    if(threadValue){
        const match = threadValue.match(/{[\s\S]*}/);
        if (match) { // AI Chatbot's Catalog Order
            const jsonString = match[0];
            try{
                const jsonObj = JSON.parse(jsonString); 
                if (jsonObj?.catalog_id && jsonObj?.product_items && jsonObj?.product_items?.length > 0) {
                    return {
                        status: true,
                        jsonObj: jsonObj,
                    }
                }
            } catch {
                return {
                    status: false,
                }
            }
        }
    }
    return {
        status: false
    }
};

export function getMessage(threadData: any) {
    threadData.chatbot_thread_value = threadData?.chatbot_thread_value?.replace("```json", "").replace("```", "");

    const chatbotThreadValue = threadData?.chatbot_thread_value;

    if (isJsonString(chatbotThreadValue)) {
        let thread = JSON.parse(chatbotThreadValue)
        if (thread.response) {
            thread = thread.response
        }
        if (typeof (thread) == "object") {
            const messageFormatId = thread?.message_format_id;
            if (messageFormatId) {
                if (messageFormatId == 1) {
                    if (thread?.message_fields?.message) {
                        if (Array.isArray(thread?.message_fields?.message)) {
                            return JSON.stringify(thread?.message_fields?.message.join("&nbsp;"))
                        } else {
                            return thread?.message_fields?.message ?? chatbotThreadValue
                        }
                    } else {
                        return chatbotThreadValue
                    }
                }
                else if (messageFormatId == 4 || messageFormatId == 3) {
                    return thread.message_fields?.url_text ?? chatbotThreadValue
                }
                else if (Math.trunc(messageFormatId) == 5 || Math.trunc(messageFormatId) == 6 || Math.trunc(messageFormatId) == 7 || Math.trunc(messageFormatId) == 8 || Math.trunc(messageFormatId) == 9 || Math.trunc(messageFormatId) == 10 || Math.trunc(messageFormatId) == 12 || Math.trunc(messageFormatId) == 13 || Math.trunc(messageFormatId) == 15 || Math.trunc(messageFormatId) == 16 || Math.trunc(messageFormatId) == 17 || Math.trunc(messageFormatId) == 18) {
                    return thread.message_reply_text ?? chatbotThreadValue
                }
                else {
                    return ((!thread?.message && !thread?.additional_text_message && !thread?.message_fields?.additional_text_message) ? chatbotThreadValue : ((thread?.message) ? thread?.message : "") + " " + ((thread?.additional_text_message) ? thread?.additional_text_message : "" + " " + ((thread?.message_fields?.additional_text_message ? thread?.message_fields?.additional_text_message : ""))))
                }
            }
            else {
                return chatbotThreadValue;
            }
        }
        else {
            return chatbotThreadValue;
        }
    }
    else {
        return chatbotThreadValue;
    }
}

// Get Thread User Name from thread metadata - Instagram
export const getThreadInstagramUserName = (
    metadata: { metadata_type_name: string; metadata_value: string }[]
): string => {
    const userName = getMetadataValue(metadata, METADATA_KEYS.USER_NAME);
    return userName ? `@${userName}` : "";
}; 

// Mirrors the UI's display timestamp (end || start); handles epoch-ms strings and ISO strings.
export const getThreadMessageTimeMs = (message: { step_end_timestamp?: string | number | null, step_start_timestamp?: string | number | null }): number => {
    const value = message?.step_end_timestamp || message?.step_start_timestamp;
    if (value === undefined || value === null || value === "") return 0;

    const ms = /^\d+$/.test(String(value)) ? Number(value) : new Date(value).getTime();
    
    return Number.isNaN(ms) ? 0 : ms;
};
